import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { rosterApi } from '../api/auth';
import { Button } from '../ui/Button';
import { SelectField, TextField } from '../ui/Field';

function ErrorBanner({ message }: { message: string }) {
  return (
    <p
      className="t-body"
      role="alert"
      style={{
        background: 'var(--color-status-blocked-surface)',
        border: '1px solid var(--color-status-blocked)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        color: 'var(--color-text-primary)',
        margin: 0,
      }}
    >
      {message}
    </p>
  );
}

export function AddAdultForm({ unitId, onDone }: { unitId: string; onDone: () => void }) {
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [permissionLevel, setPermissionLevel] = useState('Member');
  const [positionTitle, setPositionTitle] = useState('Parent/Guardian');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await rosterApi.addAdult(unitId, {
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        permissionLevel,
        positionTitle: positionTitle.trim(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add this person.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
      <h3 className="t-subheading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
        Add an adult
      </h3>

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
        <TextField label="First name" required value={givenName} onChange={(e) => setGivenName(e.target.value)} />
        <TextField label="Last name" required value={familyName} onChange={(e) => setFamilyName(e.target.value)} />
      </div>

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        // Not required: a roster entry is useful before anyone has an account, and
        // the address is what a Google sign-in later matches against.
        hint="Needed before they can sign in. You can add it later."
      />

      {/* Adults only, everywhere. The youth form has no equivalent and will not get one: rule 10
          keeps a leader from contacting a Scout off-system, and a phone number is that same
          hazard in a more immediate form. TROOP-132. */}
      <TextField
        label="Phone number"
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        maxLength={40}
        hint="Printed on the roster a leader takes on a trip. You can add it later."
      />

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
        <TextField
          label="Position"
          required
          value={positionTitle}
          onChange={(e) => setPositionTitle(e.target.value)}
          hint="Scoutmaster, Committee Chair, Parent/Guardian…"
        />

        <SelectField
          label="Permissions"
          required
          value={permissionLevel}
          onChange={(e) => setPermissionLevel(e.target.value)}
          hint="Admin can change the roster. Officer can run the program."
        >
          <option value="Member">Member</option>
          <option value="Officer">Officer</option>
          <option value="Admin">Admin</option>
        </SelectField>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="flex gap-3">
        <Button
          variant="primary"
          type="submit"
          disabledReason={
            !givenName.trim() || !familyName.trim() ? 'Enter a first and last name.' : undefined
          }
        >
          {busy ? 'Adding…' : 'Add to roster'}
        </Button>
      </div>
    </form>
  );
}

interface GuardianDraft {
  key: number;
  givenName: string;
  familyName: string;
  email: string;
}

/**
 * One form, guardians included — not two steps.
 *
 * A youth may not hold a membership without an active adult guardian, so
 * "create the Scout, then add a parent" would pass through a state the domain
 * rejects. The API takes both together, and this form matches it.
 */
export function AddYouthForm({ unitId, onDone }: { unitId: string; onDone: () => void }) {
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [positionTitle, setPositionTitle] = useState('Scout');
  const [guardians, setGuardians] = useState<GuardianDraft[]>([
    { key: 1, givenName: '', familyName: '', email: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const named = guardians.filter((g) => g.givenName.trim() && g.familyName.trim());
  const missingEmail = named.some((g) => !g.email.trim());

  function update(key: number, patch: Partial<GuardianDraft>) {
    setGuardians((current) => current.map((g) => (g.key === key ? { ...g, ...patch } : g)));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await rosterApi.addYouth(unitId, {
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        dateOfBirth: dateOfBirth || null,
        guardians: named.map((g) => ({
          givenName: g.givenName.trim(),
          familyName: g.familyName.trim(),
          email: g.email.trim() || null,
        })),
        positionTitle: positionTitle.trim(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add this youth.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
      <h3 className="t-subheading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
        Add a youth
      </h3>

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
        <TextField label="First name" required value={givenName} onChange={(e) => setGivenName(e.target.value)} />
        <TextField label="Last name" required value={familyName} onChange={(e) => setFamilyName(e.target.value)} />
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
        <TextField
          label="Date of birth"
          type="date"
          required
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          // Required because youth/adult status is derived from it and dated: the
          // rules change on their 18th birthday, on their 18th birthday.
          hint="Used to apply youth protection rules, including when they turn 18."
        />
        <TextField
          label="Position"
          required
          value={positionTitle}
          onChange={(e) => setPositionTitle(e.target.value)}
        />
      </div>

      <div
        style={{
          borderTop: '1px solid var(--color-border-subtle)',
          paddingTop: 'var(--space-4)',
        }}
      >
        <h4 className="t-label" style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}>
          Parents and guardians
        </h4>
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}
        >
          At least one is required. Add more if you need to.
        </p>
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}
        >
          {/* The service resolves a guardian by address and reuses the existing
              person, so siblings share one parent record. Saying so stops a leader
              worrying they are about to create a second Reese Lindqvist. */}
          Adding a sibling, or a parent who is already here? Use the same email address
          and it links to the account they already have — they won&rsquo;t sign up twice,
          and both children show up on their family page.
        </p>

        <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
          {guardians.map((guardian, index) => (
            <div
              key={guardian.key}
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))' }}
            >
              <TextField
                label={`Guardian ${index + 1} first name`}
                required={index === 0}
                value={guardian.givenName}
                onChange={(e) => update(guardian.key, { givenName: e.target.value })}
              />
              <TextField
                label="Last name"
                required={index === 0}
                value={guardian.familyName}
                onChange={(e) => update(guardian.key, { familyName: e.target.value })}
              />
              <TextField
                label="Email"
                type="email"
                // Required, and not as a formality. Without one a guardian can
                // never claim an account, so they can never read the threads
                // their child is in — while still being an adult who could be
                // counted toward the two-adult rule.
                required
                value={guardian.email}
                onChange={(e) => update(guardian.key, { email: e.target.value })}
                hint={
                  index === 0
                    ? 'How they sign in and see the conversations their child is in.'
                    : undefined
                }
              />

              {guardians.length > 1 && (
                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setGuardians((current) => current.filter((g) => g.key !== guardian.key))
                    }
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    Remove
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button
            variant="secondary"
            onClick={() =>
              setGuardians((current) => [
                ...current,
                { key: Date.now(), givenName: '', familyName: '', email: '' },
              ])
            }
          >
            <Plus size={16} aria-hidden="true" />
            Add another guardian
          </Button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="flex gap-3">
        <Button
          variant="primary"
          type="submit"
          disabledReason={
            !givenName.trim() || !familyName.trim()
              ? 'Enter the young person’s name.'
              : !dateOfBirth
                ? 'A date of birth is needed to apply youth protection rules.'
                : named.length === 0
                  ? 'Add at least one parent or guardian.'
                  : missingEmail
                    ? 'Every guardian needs an email so they can sign in and set up their child’s account.'
                    : undefined
          }
        >
          {busy ? 'Adding…' : 'Add to roster'}
        </Button>
      </div>
    </form>
  );
}
